import React, { useState, ChangeEvent, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  CardMedia,
  Input,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useAddPortfolioMutation,
  useLazyGetPortfolioDetailsQuery,
  useUpdatePortfolioMutation,
} from "../../service/portfolio";
import { Category, PortfolioDetails } from "../../types/General";
import { useFormik } from "formik";
import * as Yup from "yup";


import { useLazyGetCategoryQuery, useLazyGetCmsQuery } from "../../service/main";
import { useLazyGetSubCategoryQuery } from "../../service/services";
import { useLazyMyProfileQuery } from "../../service/auth";
import { showError, showToast } from "../../constants/toasts";
import dayjs from "dayjs";
import { UploadMedia, UploadVideo } from "../../utils/mediaUpload";
import Loader from "../../constants/Loader";
import { useImageVerification } from "../../hooks/imageVerification";
import { CircularProgress } from "@mui/material";

const AddPortfolio = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [images, setImages] = useState<string[]>([]);

  const [video, setVideo] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [portfolio, setPortfolio] = useState<any>(null);
  const [getCategory] = useLazyGetCategoryQuery();
  const [getSubCategory] = useLazyGetSubCategoryQuery();
  const [getPortfolioDetails, { isLoading: getPortfolioDetailsLoading }] = useLazyGetPortfolioDetailsQuery();
  const [myProfile] = useLazyMyProfileQuery();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addPortfolio, { isLoading: isAdding }] = useAddPortfolioMutation();
  const [updatePortfolio, { isLoading: isUpdating }] =
    useUpdatePortfolioMutation();
  const { validateImage, loading: loadingImage } = useImageVerification();
  const [isImageProcessing, setIsImageProcessing] = useState<boolean>(false);
  const [loadingVideo, setLoadingVideo] = useState<boolean>(false);
  const [dimensionError, setDimensionError] = useState<string | null>(null);

  const [getCms, { isLoading: isCmsLoading }] = useLazyGetCmsQuery();
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [guidelinesContent, setGuidelinesContent] = useState("");

  const handleViewGuidelines = async () => {
    try {
      const res = await getCms({}).unwrap();
      const cmsData = res?.data || {};
      const content = cmsData.guidelines || cmsData.termsAndConditionsSp || cmsData.legalSp || "<p>No guidelines found.</p>";
      setGuidelinesContent(content);
      setGuidelinesOpen(true);
    } catch (e: any) {
      showError(e?.data?.message || "Failed to load guidelines");
    }
  };

  // Handle image upload

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newFiles = formik.values.files.filter((_, i) => i !== index);

    setImages(newImages);
    formik.setFieldValue("files", newFiles);
  };

  // Handle video upload
  const handleVideoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setLoadingVideo(true);
      try {
        const uploadRes = await UploadVideo(file, true);
        if (uploadRes.statusCode === 200 || uploadRes.statusCode === 201) {
          const url =
            uploadRes.data?.url ||
            uploadRes.data?.data?.url ||
            uploadRes.data?.imageUrl ||
            uploadRes.data?.image ||
            (typeof uploadRes.data === "string" ? uploadRes.data : "");
          if (url) {
            setVideo(url);
            setVideoFile(null); // Mark as uploaded
            showToast("Video uploaded successfully.");
          }
        } else {
          showError("Failed to upload video");
        }
      } catch (err) {
        showError("Failed to upload video");
      } finally {
        setLoadingVideo(false);
      }
    }
  };

  const getPortfolioByIdApi = async () => {
    try {
      const response = await getPortfolioDetails({
        id,
      }).unwrap();
      if (response.statusCode === 200) {
        const data: PortfolioDetails = response?.data;
        setPortfolio(data);

        // Initialize images state
        if (data.media) {
          const serviceImages = data.media
            .filter((m: any) => String(m.type) === "1")
            .map((m: any) => m.url);
          setImages(serviceImages);
        }
        let videos = data?.media?.find((item) => item?.type == 2);
        setVideo(videos ? videos?.url : null);
        setVideoFile(null);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const fetchCategories = async () => {
    try {
      const response = await getCategory({}).unwrap();
      if (response.statusCode === 200) {
        setCategories(
          response?.data?.data?.filter(
            (cat: any) => !cat.isBlocked && !cat.isDeleted
          ) || []
        );
      }
    } catch (error) {
      console.error(error);
    }
  };
  const getSubCategoryApi = async () => {
    try {
      const response = await getSubCategory({
        parentId: formik.values.category,
      }).unwrap();
      if (response.statusCode === 200) {
        setSubCategories(
          response?.data?.data?.filter(
            (cat: any) => !cat.isBlocked && !cat.isDeleted
          ) || []
        );
      } else {
        setSubCategories([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const validationSchema = Yup.object({
    category: Yup.string()
      .notOneOf(["default"], "Category is required")
      .required("Category is required"),
    subCategory: Yup.string()
      .notOneOf(["default"], "Sub-category is required")
      .required("Sub-category is required"),
    name: Yup.string()
      .required("Service name is required")
      .min(3, "Name must be at least 3 characters")
      .max(100, "Name must not exceed 100 characters")
      .matches(/^\S/, "Service name cannot start with a space"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(1000, "Description must not exceed 1000 characters")
      .matches(/^\S/, "Description cannot start with a space"),
    completionDate: Yup.string()
      .required("Date of completion is required")
      .test(
        "is-not-future",
        "Date of completion cannot be in the future",
        (value) => {
          if (!value) return true;
          return dayjs(value).isBefore(dayjs().add(1, "day").startOf("day"));
        }
      ),
    files: Yup.array()
      .min(1, "At least one image is required")
      .max(7, "You can only upload up to 7 images")
      .required("At least one image is required"),
  });
  const formik = useFormik({
    initialValues: {
      category: portfolio?.category?.id || "default",
      subCategory: portfolio?.subCategory?.id || "default",
      name: portfolio?.name || "",
      description: portfolio?.description || "",
      completionDate: portfolio?.date || "",
      check1: id ? true : false,
      check2: id ? true : false,
      check3: id ? true : false,
      files: (portfolio?.media
        ? new Array(
          portfolio?.media?.filter((m: any) => String(m.type) === "1").length
        ).fill(null)
        : []) as (File | null)[],
    },
    enableReinitialize: true,

    validationSchema,
    onSubmit: async (values) => {
      if (!values.check1 || !values.check2 || !values.check3) {
        showError("Please agree to all terms and conditions.");
        return;
      }
      setIsSubmitting(true);
      try {
        // Upload Images and merge with existing ones
        const media = await Promise.all(
          images?.map(async (image, i) => {
            if (formik.values.files[i]) {
              const res = await UploadMedia(formik.values.files[i], false, true);
              return {
                type: 1,
                url:
                  res.data?.url ||
                  res.data?.data?.url ||
                  res.data?.imageUrl ||
                  res.data?.image ||
                  (typeof res.data === "string" ? res.data : ""),
              };
            } else if (image && !image.startsWith("blob:")) {
              return {
                type: 1,
                url: image,
              };
            }
            return null;
          })
        );

        const filteredMedia = media.filter((m) => m !== null);

        // Handle video upload
        if (videoFile) {
          const res = await UploadVideo(videoFile, true);
          filteredMedia.push({
            type: 2,
            url:
              res.data?.imageUrl ||
              res.data?.image ||
              res.data?.url ||
              (typeof res.data === "string" ? res.data : ""),
          });
        } else if (video && !video.startsWith("blob:")) {
          // existing video in edit mode
          filteredMedia.push({
            type: 2,
            url: video,
          });
        }

        const body = {
          categoryId: values.category,
          subCategoryId: values.subCategory,
          name: values.name,
          description: values.description,
          date: values.completionDate,
          media: filteredMedia,
        };

        let response;
        if (id) {
          response = await updatePortfolio({ id: id || "", body }).unwrap();
        } else {
          response = await addPortfolio(body).unwrap();
        }

        if (response.statusCode === 200 || response.statusCode === 201) {
          showToast(
            id ? "Service updated successfully" : "Service added successfully"
          );
          navigate("/portfolio");
        } else {
          showError(response.message || "Something went wrong");
        }
      } catch (error: any) {
        showError(error?.data?.message || "An error occurred");
      } finally {
        setIsSubmitting(false);
      }
    },
  });
  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      setDimensionError(null); // Clear previous dimension error
      if (images.length + selectedFiles.length > 7) {
        formik.setFieldError("files", "You can only upload up to 7 images.");
        formik.setFieldTouched("files", true, false);
        return;
      }

      setIsImageProcessing(true);
      const newImages = [...images];
      const newFiles = [...formik.values.files];

      try {
        for (const file of Array.from(selectedFiles)) {
          if (!file.type.startsWith("image/")) {
            showError("Unsupported image format.");
            continue;
          }
          const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
          if (file.size > MAX_FILE_SIZE) {
            showError("File size exceeds the allowed limit");
            continue;
          }

          const result = await validateImage(file);
          if (result.valid) {

            const uploadRes = await UploadMedia(file, false, true);
            if (uploadRes.statusCode === 200 || uploadRes.statusCode === 201) {
              const url =
                uploadRes.data?.url ||
                uploadRes.data?.data?.url ||
                uploadRes.data?.imageUrl ||
                uploadRes.data?.image ||
                (typeof uploadRes.data === "string" ? uploadRes.data : "");
              if (url) {
                newImages.push(url);
                newFiles.push(null); // Mark as uploaded
                showToast("Image uploaded successfully.");
              }
            } else {
              showError("Upload failed due to a network/server error");
            }
          } else {
            showError(result.error);
          }
        }
        setImages(newImages);
        formik.setFieldValue("files", newFiles);
      } catch (err) {
        showError("Upload failed due to a network/server error");
      } finally {
        setIsImageProcessing(false);
      }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formik.values.category !== "" && formik.values.category !== "default") {
      getSubCategoryApi();
    }
  }, [formik.values.category]);

  // When editing, fetch subcategories for the portfolio's category so the
  // saved subCategory name renders correctly in the dropdown.
  useEffect(() => {
    if (id && portfolio?.categoryId) {
      getSubCategory({ parentId: portfolio.categoryId })
        .unwrap()
        .then((res) => {
          if (res.statusCode === 200) {
            setSubCategories(
              res?.data?.data?.filter((cat: any) => !cat.isBlocked) || []
            );
          }
        })
        .catch(() => { });
    }
  }, [portfolio?.categoryId]);
  const handleRemoveVideo = () => {
    setVideo(null);
    setVideoFile(null);
  };


  useEffect(() => {
    if (id) {
      getPortfolioByIdApi();
    } else {
      myProfile({}).unwrap().then((res) => {
        if (res.statusCode === 200) {
          setUserProfile(res.data);
          const sp = res.data?.sp;
          if (sp) {
            let catId = sp.categoryId || sp.category;
            if (!catId && sp.categories && sp.categories.length > 0) {
              const cat = sp.categories[0];
              catId = cat.categoryId || cat._id || cat;
            }
            if (catId) {
              formik.setFieldValue("category", String(catId));
            }
          }
        }
      }).catch(console.error);
    }
  }, [id, userProfile]);
  return (
    <>
      <Loader isLoad={isSubmitting || getPortfolioDetailsLoading} />
      <div className="main_title">
        <h1>{id ? "Edit Portfolio" : "Add Portfolio"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/portfolio")}>
            Portfolio Management
          </button>{" "}
          - {id ? "Edit Portfolio" : "Add Portfolio"}
        </p>
      </div>

      <div className="cards custom_form_details">
        <div className="form">
          <div className="gap_m w_100">
            {/* Title of Work */}
            <div className="control_group w_50">
              <label htmlFor="portfolio-name">Service Name *</label>
              <TextField
                id="portfolio-name"
                placeholder="E.g., Premium Home Cleaning"
                fullWidth
                name="name"
                value={formik.values.name}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.name && !!formik.errors.name}
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Description */}
            <div className="control_group w_50">
              <label htmlFor="portfolio-description">Description *</label>
              <TextField
                id="portfolio-description"
                placeholder="Provide a brief description..."
                fullWidth
                multiline
                minRows={3}
                name="description"
                value={formik.values.description}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description && !!formik.errors.description
                }
                helperText={
                  formik.touched.description &&
                  (formik.errors.description as string)
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Category */}
            <div className="control_group w_50">
              <label htmlFor="portfolio-category">Category *</label>
              <Select
                fullWidth
                name="category"
                value={formik.values.category}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldValue("subCategory", "default");
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.category && !!formik.errors.category}
                displayEmpty
                disabled
                inputProps={{ id: "portfolio-category" }}

              >
                <MenuItem value="default" disabled>
                  Select Category
                </MenuItem>
                {categories?.map((cat) => (
                  <MenuItem key={cat?.id} value={cat?.id}>
                    {cat?.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <p className="err_txt">
                  {formik.errors.category as string}
                </p>
              )}
            </div>

            {/* Sub-category */}
            <div className="control_group w_50">
              <label htmlFor="portfolio-subCategory">Sub-category *</label>
              <Select
                fullWidth
                name="subCategory"
                value={formik.values.subCategory}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subCategory && !!formik.errors.subCategory
                }
                displayEmpty
                inputProps={{ id: "portfolio-subCategory" }}
              >
                <MenuItem value="default" disabled>
                  Select Sub-category
                </MenuItem>
                {subCategories?.map((sub) => (
                  <MenuItem key={sub?.id} value={sub?.id}>
                    {sub?.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subCategory && formik.errors.subCategory && (
                <p className="err_txt">
                  {formik.errors.subCategory as string}
                </p>
              )}
            </div>

            {/* Date of Completion */}
            <div className="control_group w_50">
              <label htmlFor="portfolio-completionDate">Date of Completion *</label>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disableFuture
                  value={
                    formik.values.completionDate
                      ? dayjs(formik.values.completionDate).local()
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "completionDate",
                      date ? date.startOf("day").toISOString() : ""
                    );
                  }}
                  slotProps={{
                    textField: {
                      id: "portfolio-completionDate",
                      fullWidth: true,
                      error:
                        formik.touched.completionDate &&
                        Boolean(formik.errors.completionDate),
                      helperText:
                        formik.touched.completionDate &&
                          formik.errors.completionDate
                          ? String(formik.errors.completionDate)
                          : "",
                      FormHelperTextProps: {
                        className: "err_txt",
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="cards upload_section">
        <Typography variant="h6" fontWeight={600} mb={2}>
          Upload Media
        </Typography>

        {/* Images Upload */}
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          Upload Images (Multiple Allowed) (Recommended: 375*200 px)*
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, 120px)"
          gap={2}
          mt={1}
        >
          <label className="upload_image" htmlFor="icon-button-file-multi">
            <Input
              sx={{ display: "none" }}
              id="icon-button-file-multi"
              type="file"
              inputProps={{
                accept: "image/*",
                multiple: true,
              }}
              onChange={handleImageChange}
            />
            <span className="upload_image_holder square">
              <figure className="add_img">
                <img src="/static/images/placeholder.png" alt="icon" />
                <span className="upload_img_icon">
                  <img src="/static/images/edit2_icon.svg" alt="edit" />
                </span>
                {(isImageProcessing || loadingImage) && (
                  <div className="image_loader_overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.7)',
                    borderRadius: '8px'
                  }}>
                    <CircularProgress size={24} />
                  </div>
                )}
              </figure>
            </span>
          </label>

          {/* Previews */}
          {images.map((image, i) => (
            <div key={i} className="upload_image">
              <div className="upload_image_holder square">
                <figure>
                  <CardMedia
                    component="img"
                    image={image || ""}
                    alt="icon"
                    sx={{ objectFit: 'cover' }}
                  />
                </figure>
                <CloseIcon onClick={() => handleRemoveImage(i)} />
              </div>
            </div>
          ))}
        </Box>
        {(dimensionError || ((formik.touched.files || formik.submitCount > 0) && formik.errors.files && typeof formik.errors.files === "string")) && (
          <p className="err_txt">
            {dimensionError || formik.errors.files}
          </p>
        )}

        {/* Video Upload */}
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={1}>
            Upload Video (Optional)
          </Typography>

          {video ? (
            <div className="upload_image video_upload">
              <video
                width="220"
                height="140"
                controls
                style={{ borderRadius: "8px" }}
              >
                <source src={video} type="video/mp4" />
                <track kind="captions" label="Captions" default />
              </video>
              <CloseIcon onClick={handleRemoveVideo} />
            </div>
          ) : (
            <label htmlFor="video-upload" className="upload_image">
              <Input
                id="video-upload"
                type="file"
                sx={{ display: "none" }}
                inputProps={{
                  accept: "video/*",
                }}
                onChange={handleVideoChange}
              />
              <span className="upload_image_holder square">
                <figure className="add_img">
                  <img src="/static/images/placeholder.png" alt="icon" />
                  <span className="upload_img_icon">
                    <img src="/static/images/edit2_icon.svg" alt="edit" />
                  </span>
                  {loadingVideo && (
                    <div className="image_loader_overlay" style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.7)',
                      borderRadius: '8px'
                    }}>
                      <CircularProgress size={24} />
                    </div>
                  )}
                </figure>
              </span>
            </label>
          )}
        </Box>
        {/* Confirmation Checkboxes */}
        <Box mt={4}>
          <div className="control_group checkbox_group">
            <label
              style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
            >
              <input
                type="checkbox"
                name="check1"
                checked={formik.values.check1}
                onChange={formik.handleChange}
              />
              <span>
                I confirm that I own the rights to these photos and services,
                and that I have written consent from any identifiable people
                shown to use these photos.
              </span>
            </label>
          </div>

          <div className="control_group checkbox_group">
            <label
              style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
            >
              <input
                type="checkbox"
                name="check2"
                checked={formik.values.check2}
                onChange={formik.handleChange}
              />
              <span>I confirm the uploaded creatives meet the guidelines.</span>
            </label>
          </div>

          <div className="control_group checkbox_group">
            <label
              style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
            >
              <input
                type="checkbox"
                name="check3"
                checked={formik.values.check3}
                onChange={formik.handleChange}
              />
              <span>I agree to the Marketplace Advertising Terms.</span>
            </label>
          </div>

          {/* View Guidelines Button */}
          <Box mt={2} mb={2}>
            <Button variant="outlined" className="btn btn_secondary" onClick={handleViewGuidelines} disabled={isCmsLoading}>
              {isCmsLoading ? "Loading..." : "View Guidelines"}
            </Button>
          </Box>
        </Box>

        {/* Save Button */}
        <div className="w_100 mt_20">
          <Button
            className="btn btn_primary w_100"
            onClick={() => formik.handleSubmit()}
            disabled={isAdding || isUpdating}
          >
            {id ? "Update Portfolio" : "Add Portfolio"}
          </Button>
        </div>
      </div>

      <Dialog open={guidelinesOpen} onClose={() => setGuidelinesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #eee' }}>Guidelines</DialogTitle>
        <DialogContent dividers>
          <div className="cms_content" dangerouslySetInnerHTML={{ __html: guidelinesContent }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuidelinesOpen(false)} variant="contained" className="btn btn_primary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPortfolio;